// Derived product counts + bundle math.
//
// Single source of truth: the product registry in lib/products.ts. NEVER
// hardcode extension counts or bundle totals in copy — import from here so
// every mention updates automatically when a product is added.
//
// Counting rules (decided Jul 2026):
// - PRODUCT_COUNT includes coming-soon products — if it has a page on the
//   website, it counts.
// - PAID_PRODUCT_COUNT (bundle math) = products with an `offers` block.
//   Free-forever products (Enhanced PDF Viewer) have no Pro to unlock, so
//   they are excluded from bundle value/savings claims.
// - Savings percentages use Math.floor so marketing copy never overstates.
//
// NOTE: this module calls getAllProducts() at import time. Do not import it
// from lib/products.ts or any lib/data/products/* file (circular import).

import { getAllProducts, getLiveProducts } from "./products";
import { PRICING } from "./pricing-constants";

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
] as const;

function toWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

const allProducts = getAllProducts();
const paidProducts = allProducts.filter((p) => p.offers);

/** All products on the website, including coming-soon. e.g. 10 */
export const PRODUCT_COUNT = allProducts.length;

/** PRODUCT_COUNT as a lowercase word, e.g. "ten" */
export const PRODUCT_COUNT_WORD = toWord(PRODUCT_COUNT);

/**
 * Products actually published on the Chrome Web Store (excludes coming-soon).
 * Use ONLY for claims tied to store presence, e.g. "passed CWS review". e.g. 9
 */
export const LIVE_PRODUCT_COUNT = getLiveProducts().length;

/** LIVE_PRODUCT_COUNT as a lowercase word, e.g. "nine" */
export const LIVE_PRODUCT_COUNT_WORD = toWord(LIVE_PRODUCT_COUNT);

/** Products with a Pro tier (bundle-relevant), including coming-soon. e.g. 9 */
export const PAID_PRODUCT_COUNT = paidProducts.length;

/** PAID_PRODUCT_COUNT as a lowercase word, e.g. "nine" */
export const PAID_PRODUCT_COUNT_WORD = toWord(PAID_PRODUCT_COUNT);

/** PAID_PRODUCT_COUNT_WORD capitalized for sentence starts, e.g. "Nine" */
export const PAID_PRODUCT_COUNT_WORD_TITLE =
  PAID_PRODUCT_COUNT_WORD.charAt(0).toUpperCase() + PAID_PRODUCT_COUNT_WORD.slice(1);

const bundlePrice = parseFloat(PRICING.BUNDLE_LIFETIME);
const fullValue = PAID_PRODUCT_COUNT * parseFloat(PRICING.LIFETIME);

/** Sum of individual lifetime licenses, e.g. "449.91" */
export const BUNDLE_FULL_VALUE = fullValue.toFixed(2);

/** Bundle discount vs individual lifetimes, floored, e.g. "66" */
export const BUNDLE_SAVINGS_PERCENT = String(Math.floor((1 - bundlePrice / fullValue) * 100));

/** Absolute savings vs individual lifetimes, e.g. "299.92" */
export const BUNDLE_SAVINGS_AMOUNT = (fullValue - bundlePrice).toFixed(2);

/** Effective bundle cost per Pro extension, e.g. "16.67" */
export const BUNDLE_PER_EXTENSION = (bundlePrice / PAID_PRODUCT_COUNT).toFixed(2);

// ---------------------------------------------------------------------------
// Social-proof aggregates
//
// These exist so pages stop hard-coding claims. /pricing/ carried "Trusted by
// 10,000+ users with 4.8/5 average Chrome Web Store rating" as a string
// literal, which was both unmaintainable and wrong in the conservative
// direction — the real numbers are considerably better.
//
// Two honesty constraints are baked in deliberately:
//
// 1. Ratings are weighted by reviewCount, and products with reviewCount 0 are
//    EXCLUDED. Four products carry a placeholder ratingValue with no reviews
//    behind it; averaging those in would manufacture a rating out of nothing.
//    This is the same line already drawn elsewhere — see the commits that
//    scrubbed fabricated claims and removed self-serving review schema.
//
// 2. Installs are reported as installs, never as "users". The per-product
//    counts are Chrome Web Store install figures, so someone running three of
//    our extensions is counted three times. Summing them and calling the
//    result "users" would overstate reach.
// ---------------------------------------------------------------------------

const ratedProducts = allProducts.filter((p) => {
  const reviews = parseInt(p.aggregateRating?.reviewCount ?? "0", 10);
  return Number.isFinite(reviews) && reviews > 0;
});

const totalReviews = ratedProducts.reduce(
  (sum, p) => sum + parseInt(p.aggregateRating?.reviewCount ?? "0", 10),
  0
);

const weightedRatingSum = ratedProducts.reduce((sum, p) => {
  const rating = parseFloat(p.aggregateRating?.ratingValue ?? "0");
  const reviews = parseInt(p.aggregateRating?.reviewCount ?? "0", 10);
  return sum + rating * reviews;
}, 0);

/** Total Chrome Web Store reviews across products that actually have any, e.g. "503" */
export const TOTAL_REVIEW_COUNT = String(totalReviews);

/** Review-weighted average rating, rated products only, e.g. "4.9" */
export const AVERAGE_RATING =
  totalReviews > 0 ? (weightedRatingSum / totalReviews).toFixed(1) : "0.0";

/**
 * Total installs across all extensions, floored to the nearest 10k and
 * formatted, e.g. "100,000". Installs, not unique people — see note above.
 */
const totalInstalls = allProducts.reduce(
  (sum, p) => sum + (parseInt((p.userCount ?? "0").replace(/,/g, ""), 10) || 0),
  0
);
export const TOTAL_INSTALLS_ROUNDED = (Math.floor(totalInstalls / 10_000) * 10_000).toLocaleString(
  "en-US"
);
